import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Users,
  Award,
  Globe2,
  Monitor,
  Smartphone,
  BarChart3,
  Radio,
  QrCode,
  Handshake,
  ChevronDown,
  ChevronLeft,
  Zap,
  Rocket,
  ShieldCheck,
  Search,
  ClipboardList,
  Truck,
  BellRing,
  Video,
  MapPinned,
  Database,
  Megaphone,
  TrendingUp,
  Palette,
  LayoutGrid,
  Play,
  Pause,
  Volume2,
  VolumeX,
  PlayCircle,
  Beaker,
  GraduationCap,
  Building2,
  Star,
  BadgeCheck,
  Download,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";
import MaskedReveal from "@/components/motion/MaskedReveal";
import Seo, {
  ORGANIZATION_JSONLD,
  WEBSITE_JSONLD,
  AGGREGATE_RATING_JSONLD,
  LOCAL_BUSINESS_JSONLD,
  buildFaqJsonLd,
} from "@/components/Seo";
import EditorialMarquee from "@/components/sections/EditorialMarquee";
import ClientLogos from "@/components/sections/ClientLogos";
import Testimonials from "@/components/sections/Testimonials";
import EventSimulator from "@/components/sections/EventSimulator";
import ReelLightbox from "@/components/ReelLightbox";
import AnimatedCounter from "@/components/AnimatedCounter";
import Magnetic from "@/components/motion/Magnetic";
import HeroCursorTrail from "@/components/motion/HeroCursorTrail";
import { IMAGES, LOGO_URL } from "@/constants/site";
import { HERO } from "@/constants/testIds";
import ParticleField from "@/components/motion/ParticleField";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const heroSlides = [
  {
    id: "concierge",
    image: IMAGES.hero,
    chip: "Full-Service Concierge · Global",
    chipTone: "cyan",
    headline: (
      <>
        Full-service event <br className="hidden md:block" />
        &amp; meeting concierge{" "}
        <span className="hero-accent">
          at scale.
        </span>
      </>
    ),
    subtext:
      "Certified project management and event-technology experts orchestrating meetings, associations, and productions across the globe — engineered to enhance experience, scale revenue, and deliver with confidence.",
    primaryCta: { label: "Request a Quote", to: "/contact" },
    secondaryCta: { label: "See Capabilities", to: "/services" },
  },
  {
    id: "tech",
    video:
      "https://videos.pexels.com/video-files/8246440/8246440-hd_1920_1080_25fps.mp4",
    poster: IMAGES.tech,
    chip: 'Event Tech Concierge · Modular Approach',
    chipTone: "cyan",
    headline: (
      <>
        Event Tech Concierge —{" "}
        <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
          Engineered as one solution.
        </span>
      </>
    ),
    subtext:
      'Registration, mobile, hybrid broadcast, data, and sponsorship — architected so attendees feel one continuous experience. A full Concierge "tech" services solution.',
    primaryCta: { label: "Explore Our Services", to: "/services" },
    secondaryCta: { label: "Request a Quote", to: "/contact" },
  },
  // ── DISABLED (kept for future re-enable) ──────────────────────────
  // The "reel" slide was temporarily removed from the hero rotation
  // per client request. Restore by un-commenting the object below.
  // {
  //   id: "reel",
  //   video:
  //     "https://videos.pexels.com/video-files/3209828/3209828-uhd_3840_2160_25fps.mp4",
  //   poster:
  //     "https://images.pexels.com/videos/3209828/free-video-3209828.jpg",
  //   chip: "Behind the shows · Our Reel",
  //   chipTone: "cyan",
  //   headline: (
  //     <>
  //       The rooms we build.{" "}
  //       <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
  //         Watch the reel.
  //       </span>
  //     </>
  //   ),
  //   subtext:
  //     "A cinematic look at recent client programs — corporate conferences, associations, and executive tours delivered end-to-end. Real audiences. Real outcomes.",
  //   primaryCta: { label: "Watch our reel", to: "/#featured-reel" },
  //   secondaryCta: { label: "Request a Quote", to: "/contact" },
  // },
];

// Rotating hero trust badge — one-line client quotes
const HERO_TRUST_QUOTES = [
  { quote: "Sir Dion carries my highest recommendation.", author: "Prof. Joe Goldblatt" },
  { quote: "Phenomenal — saved money, optimized attendee experience.", author: "Dr. Tiffani Worthy, PMP" },
  { quote: "White-glove service. A seasoned meeting professional.", author: "Joyce Wooden" },
  { quote: "One of the finest instructors I've worked with.", author: "Ira Rosen, MA, CFEE" },
  { quote: "Dion gets it — team-first, planner-first, guest-first.", author: "Nancy Bakewell" },
  { quote: "Amazing to work with — a sincere pleasure.", author: "Tangan Lee · CBS Studios" },
  { quote: "Complex ideas made easy. I always felt his support.", author: "Pita Cruz y Curro · Volvo Group" },
  { quote: "Best class I've taken. Dynamic and insightful.", author: "Maya Turner, CGSP" },
];

function HeroTrustBadge() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % HERO_TRUST_QUOTES.length);
    }, 5500);
    return () => clearInterval(t);
  }, []);
  const t = HERO_TRUST_QUOTES[idx];
  return (
    <div className="mt-10 flex justify-center px-4" data-testid="hero-trust-badge">
      <div className="flex flex-col items-center gap-2 sm:gap-2.5 px-5 sm:px-7 py-3 sm:py-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white/90 max-w-[92vw] sm:max-w-2xl text-center">
        <span className="flex items-center gap-0.5 text-[#FBBF24] shrink-0" aria-label="5 star rating">
          <Star size={12} fill="currentColor" strokeWidth={0} />
          <Star size={12} fill="currentColor" strokeWidth={0} />
          <Star size={12} fill="currentColor" strokeWidth={0} />
          <Star size={12} fill="currentColor" strokeWidth={0} />
          <Star size={12} fill="currentColor" strokeWidth={0} />
        </span>
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center gap-1.5"
          >
            <span className="text-[12px] sm:text-[13px] md:text-[14px] italic font-light leading-snug max-w-xl">
              &ldquo;{t.quote}&rdquo;
            </span>
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-cyan-300 shrink-0">
              — {t.author}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function FeaturedVideo() {
  const iframeRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const send = (msg) => {
      try {
        iframe.contentWindow?.postMessage(JSON.stringify(msg), "*");
      } catch (_) { /* iframe not ready */ }
    };

    const subscribe = () => {
      send({ method: "addEventListener", value: "play" });
      send({ method: "addEventListener", value: "pause" });
      send({ method: "addEventListener", value: "ended" });
    };

    iframe.addEventListener("load", subscribe);

    const onMessage = (event) => {
      if (typeof event.data !== "string") return;
      let data;
      try { data = JSON.parse(event.data); } catch (_) { return; }
      if (!data || !data.event) return;
      if (data.event === "play") setIsPlaying(true);
      else if (data.event === "pause" || data.event === "ended") setIsPlaying(false);
    };
    window.addEventListener("message", onMessage);

    return () => {
      iframe.removeEventListener("load", subscribe);
      window.removeEventListener("message", onMessage);
    };
  }, []);

  return (
    <section id="featured-reel" className="relative py-20 md:py-32 overflow-hidden scroll-mt-24">
      <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-32 -left-40 opacity-35" />
      <div className="brand-bloom brand-bloom-warm w-[420px] h-[420px] bottom-0 -right-24 opacity-30" />
      <HexMark
        size={240}
        opacity={0.055}
        className="absolute right-6 top-16 rotate-[16deg] pointer-events-none hidden lg:block"
      />
      <span className="corner-cross" style={{ top: 40, left: 40 }} />
      <span className="corner-cross" style={{ bottom: 40, right: 40 }} />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <Reveal>
            <div>
              <span className="chip chip-warm">
                <span className="chip-dot" /> Featured &middot; Reel
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                Experiences you can{" "}
                <span className="bg-gradient-to-r from-cyan-500 via-sky-600 to-blue-600 bg-clip-text text-transparent italic font-medium">
                  feel in the room.
                </span>
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-[var(--text-muted)]">
              A short reel from recent client programs — conferences, associations,
              and executive meetings, special events, delivered end-to-end.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative group rounded-[24px] md:rounded-[32px] overflow-hidden border border-[var(--border)] shadow-[0_30px_80px_-30px_rgba(11,37,69,0.4)] bg-[#050A14]">
            {/* Vimeo responsive 16:9 wrapper */}
            <div
              style={{ padding: "56.25% 0 0 0", position: "relative" }}
              data-testid="featured-reel-video"
            >
              <iframe
                ref={iframeRef}
                src="https://player.vimeo.com/video/1167756915?badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
                title="Magee Enterprises, Event Multimedia Company"
              />
            </div>

            {/* Corner brand marker (overlaid on top of the iframe) — hides while video plays */}
            <div
              className={`absolute top-6 left-6 flex items-center gap-2 pointer-events-none z-10 transition-opacity duration-300 ${
                isPlaying ? "opacity-0" : "opacity-100"
              }`}
              aria-hidden={isPlaying}
            >
              <span className="chip !bg-white/15 !text-white !border-white/25 backdrop-blur">
                <span className="live-dot" />
                Live Reel — Magee &times; Clients
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function QuickScope() {
  const navigate = useNavigate();
  const [eventType, setEventType] = useState("");
  const [attendees, setAttendees] = useState("");
  const [timeline, setTimeline] = useState("");
  const disabled = !eventType && !attendees && !timeline;

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (eventType) params.set("event_type", eventType);
    if (attendees) params.set("attendees", attendees);
    if (timeline) params.set("event_date", timeline);
    params.set("scope", "1");
    navigate(`/contact?${params.toString()}#proposal`);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="quickscope-form"
      className="mt-10 md:mt-12 mx-auto max-w-3xl w-full bg-white/[0.08] backdrop-blur-xl border border-white/15 rounded-2xl md:rounded-full p-2 md:p-1.5 flex flex-col md:flex-row items-stretch gap-2 md:gap-0 shadow-[0_20px_50px_-20px_rgba(6,182,212,0.35)]"
    >
      <span className="hidden md:inline-flex items-center gap-2 pl-5 pr-3 text-cyan-300 font-mono-brand text-[10px] uppercase tracking-[0.22em]">
        <Sparkles size={12} /> Scope in 30s
      </span>
      <span className="md:hidden inline-flex items-center gap-2 px-3 pt-1 text-cyan-300 font-mono-brand text-[10px] uppercase tracking-[0.22em]">
        <Sparkles size={12} /> Scope in 30 seconds
      </span>
      <input
        aria-label="Event type"
        placeholder="Event type"
        value={eventType}
        onChange={(e) => setEventType(e.target.value)}
        data-testid="quickscope-event-type"
        className="flex-1 bg-transparent text-white placeholder-white/50 outline-none px-4 py-3 text-sm border-b md:border-b-0 md:border-l border-white/10"
      />
      <input
        aria-label="Attendees"
        placeholder="Attendees"
        value={attendees}
        onChange={(e) => setAttendees(e.target.value)}
        data-testid="quickscope-attendees"
        className="flex-1 bg-transparent text-white placeholder-white/50 outline-none px-4 py-3 text-sm border-b md:border-b-0 md:border-l border-white/10"
      />
      <input
        aria-label="Timeline"
        placeholder="Timeline (e.g., Q3 2026)"
        value={timeline}
        onChange={(e) => setTimeline(e.target.value)}
        data-testid="quickscope-timeline"
        className="flex-1 bg-transparent text-white placeholder-white/50 outline-none px-4 py-3 text-sm md:border-l border-white/10"
      />
      <button
        type="submit"
        disabled={disabled}
        data-testid="quickscope-submit"
        className="btn-primary md:!py-2.5 md:!px-5 !text-[12px] justify-center whitespace-nowrap"
      >
        Scope it <ArrowRight size={14} />
      </button>
    </form>
  );
}

function HeroCarousel({ yImg, opacityScroll, onReelOpen }) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);
  const AUTO_MS = 7000;

  const goTo = useCallback((i) => {
    setIndex(((i % heroSlides.length) + heroSlides.length) % heroSlides.length);
  }, []);
  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  useEffect(() => {
    if (!playing) return;
    intervalRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % heroSlides.length);
    }, AUTO_MS);
    return () => clearInterval(intervalRef.current);
  }, [playing]);

  const slide = heroSlides[index];

  return (
    <div
      ref={containerRef}
      className="relative max-w-[1600px] mx-auto rounded-[28px] md:rounded-[36px] overflow-hidden min-h-[640px] md:min-h-[720px]"
      onMouseEnter={() => setPlaying(false)}
      onMouseLeave={() => setPlaying(true)}
      data-testid="hero-carousel"
    >
      {/* Cinematic cursor light-leak (desktop, hover-capable devices only) */}
      <HeroCursorTrail targetRef={containerRef} />
      {/* Cross-fading media (video or image) */}
      <AnimatePresence mode="sync">
        <motion.div
          key={`media-${slide.id}`}
          style={{ y: yImg }}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          {slide.video ? (
            <video
              key={slide.id}
              src={slide.video}
              poster={slide.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={slide.image}
              alt={slide.chip}
              className="w-full h-full object-cover"
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050A14]/45 via-[#050A14]/35 to-[#050A14]/85 pointer-events-none" />
      <div className="absolute inset-0 grid-pattern-dark opacity-20 pointer-events-none" />
      {/* Ambient particle field (very sparse cyan dots drifting) */}
      <ParticleField count={26} className="opacity-70" />
      <div className="absolute -top-32 -left-24 w-[500px] h-[500px] rounded-full bg-cyan-400/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-24 w-[600px] h-[600px] rounded-full bg-sky-500/20 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-10 w-[280px] h-[280px] rounded-full bg-orange-500/10 blur-3xl pointer-events-none" />

      {/* 3D orbs */}

      {/* Corner crosses */}
      <span className="corner-cross hidden md:block" style={{ top: 24, left: 24, color: "#fff" }} />
      <span className="corner-cross hidden md:block" style={{ top: 24, right: 24, color: "#fff" }} />
      <span className="corner-cross hidden md:block" style={{ bottom: 24, left: 24, color: "#fff" }} />
      <span className="corner-cross hidden md:block" style={{ bottom: 24, right: 24, color: "#fff" }} />

      {/* Top-right live indicator */}
      <div className="hidden md:flex absolute top-8 right-8 items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-4 py-2 z-10">
        <span className="live-dot" />
        <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-white/90">
          Live · Accepting Bookings
        </span>
      </div>

      {/* Content */}
      <div className="relative px-4 md:px-14 pt-14 md:pt-24 pb-24 md:pb-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={`content-${slide.id}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-center">
              <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                <span className="chip-dot" />
                {slide.chip}
              </span>
            </div>

            <h1
              className="hero-headline text-white text-center mt-8 md:mt-10 leading-[0.95] text-[46px] sm:text-[60px] md:text-[76px] lg:text-[92px] max-w-5xl mx-auto text-balance px-2"
              data-testid={`hero-headline-${slide.id}`}
            >
              {slide.headline}
            </h1>

            <div className="flex justify-center mt-8 md:mt-10">
              <span className="pulse-bar w-32 md:w-48" />
            </div>

            <p className="text-center text-white/85 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mt-8">
              {slide.subtext}
            </p>

            <div className="mt-10 md:mt-12 flex flex-wrap items-center justify-center gap-4">
              {slide.id === "reel" ? (
                <Magnetic>
                  <button
                    type="button"
                    onClick={onReelOpen}
                    data-testid="hero-cta-reel-primary"
                    className="btn-primary"
                  >
                    {slide.primaryCta.label} <ArrowRight size={16} />
                  </button>
                </Magnetic>
              ) : (
                <Magnetic>
                  <Link
                    to={slide.primaryCta.to}
                    data-testid={
                      slide.id === "concierge" ? HERO.ctaProposal : "hero-cta-tech-primary"
                    }
                    className="btn-primary"
                  >
                    {slide.primaryCta.label} <ArrowRight size={16} />
                  </Link>
                </Magnetic>
              )}
              <Magnetic>
                <Link
                  to={slide.secondaryCta.to}
                  data-testid={
                    slide.id === "concierge" ? HERO.ctaServices : "hero-cta-tech-secondary"
                  }
                  className="btn-dark backdrop-blur"
                >
                  {slide.secondaryCta.label}
                </Link>
              </Magnetic>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Hero trust badge — rotating client quotes */}
        <HeroTrustBadge />

        {/* Metric strip — measurable outcomes (animated counters) */}
        <div
          className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 text-white/80"
          data-testid="hero-metric-strip"
        >
          {[
            { target: 100, suffix: "+", label: "Events Produced" },
            { target: 8000, suffix: "+", label: "Attendees Served" },
            { target: 26, suffix: "+", label: "Years Experience" },
            { target: 5, decimals: 1, suffix: "★", label: "Client Rating" },
          ].map((m, i, arr) => (
            <div key={m.label} className="flex items-center gap-6">
              <div className="flex items-baseline gap-2">
                <AnimatedCounter
                  target={m.target}
                  decimals={m.decimals || 0}
                  suffix={m.suffix}
                  duration={1400 + i * 200}
                  className="font-editorial text-lg md:text-xl leading-none text-white tabular-nums"
                />
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/80">
                  {m.label}
                </span>
              </div>
              {i < arr.length - 1 && (
                <span className="hidden md:inline-block w-px h-4 bg-white/25" />
              )}
            </div>
          ))}
        </div>

        {/* Prev / Next arrows */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          data-testid="hero-prev"
          className="hidden md:flex items-center justify-center absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white transition-all z-10"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={next}
          aria-label="Next slide"
          data-testid="hero-next"
          className="hidden md:flex items-center justify-center absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white transition-all z-10"
        >
          <ChevronDown size={18} className="-rotate-90" />
        </button>

        {/* Bottom controls: dots + play/pause + slide label */}
        <div className="mt-16 md:mt-20 flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            {heroSlides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                data-testid={`hero-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                className={`group flex items-center gap-2 transition-all`}
              >
                <span
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === index
                      ? "w-10 bg-gradient-to-r from-cyan-300 to-sky-400"
                      : "w-6 bg-white/30 group-hover:bg-white/60"
                  }`}
                />
                <span
                  className={`font-mono-brand text-[10px] uppercase tracking-[0.22em] transition-colors ${
                    i === index ? "text-white" : "text-white/40"
                  }`}
                >
                  0{i + 1}
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setPlaying((p) => !p)}
              data-testid="hero-playpause"
              aria-label={playing ? "Pause slideshow" : "Play slideshow"}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center backdrop-blur transition-all"
            >
              {playing ? <Pause size={12} /> : <Play size={12} />}
            </button>
            <span className="hidden md:inline font-mono-brand text-[10px] uppercase tracking-[0.24em] text-white/60">
              {String(index + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Scroll hint */}
        <motion.div
          style={{ opacity: opacityScroll }}
          data-testid={HERO.scrollHint}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 text-white/60 text-xs font-mono-brand uppercase tracking-[0.24em]"
        >
          Scroll
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown size={14} />
          </motion.span>
        </motion.div>
      </div>
    </div>
  );
}

const featureCards = [
  { icon: Users, title: "Expert Team", body: "Certified PMs & travel directors on the floor." },
  { icon: Award, title: "Award-Quality", body: "Broadcast-grade production and stagecraft." },
  { icon: Globe2, title: "Global Reach", body: "Programs executed on every continent." },
  { icon: ShieldCheck, title: "On-Time, Every Time", body: "Structured playbooks, zero drama on-site." },
  { icon: BadgeCheck, title: "SBA 8(a) Certified Enterprise", body: "Federally certified minority-owned partner — qualifying you for streamlined contracting and diverse-supplier goals.", link: "/government", linkLabel: "Download capabilities" },
  { icon: ClipboardList, title: "Best Industry Standards", body: "We don't just follow the rules — we helped write them." },
];

const techStack = [
  { icon: QrCode, title: "Registration Platforms", body: "Journey-first badging, check-in, and access." },
  { icon: Smartphone, title: "Mobile App Integration", body: "Native or web, tied to your registration system or attendee community." },
  { icon: Monitor, title: "Onsite Tech PMs", body: "Technologists in the room — not just Zoom." },
  { icon: Database, title: "Data Entry Management", body: "Clean, structured, exportable — real analytics." },
  { icon: Radio, title: "Virtual & Hybrid Meetings", body: "Broadcast-grade productions with global reach." },
  { icon: Megaphone, title: "Attendee Outreach", body: "Segmented pre-, mid- and post-event communications." },
  { icon: Handshake, title: "Sponsorship Management", body: "Inventory, activation delivery, and revenue." },
  { icon: TrendingUp, title: "Business Strategy & Performance", body: "Program KPIs tied to revenue and growth." },
  { icon: Palette, title: "Marketing, Social & Branding", body: "Complete branding & marketing strategies with proven growth techniques." },
  { icon: LayoutGrid, title: "Web Design & AI Custom Solutions", body: "Bespoke digital solutions and customized applications for the event." },
];

const manifesto = [
  {
    n: "01",
    title: "Enhance attendee experiences — and revenue.",
    body: "Every touchpoint engineered to lift guest value, sponsor return, and long-term brand equity — measured, not guessed.",
  },
  {
    n: "02",
    title: "Scale your events, revenue and reach.",
    body: "Growth-minded programs that expand reach, deepen engagement, and multiply the ROI of every gathering.",
  },
  {
    n: "03",
    title: "Certified PM & tech experts — delivered with confidence.",
    body: "Registration, mobile, hybrid broadcast, and data — designed as one continuous product, executed by certified PMs.",
  },
  {
    n: "04",
    title: "We work for you.",
    body: "A concierge posture. Hourly or full-service. Your team, extended — quiet, obsessive, accountable.",
  },
];

const faq = [
  {
    q: "What does full-service concierge really include?",
    a: "Everything from sourcing and contracting to on-site execution and post-event analytics. We embed as your extended team — hourly, day-of, or fully retained.",
  },
  {
    q: "Do you handle hybrid and virtual events?",
    a: "Yes. Broadcast-grade productions, native/web mobile apps, sponsorship activations, and real-time attendee analytics — engineered as a single guest experience.",
  },
  {
    q: "Can you support associations, not just corporate?",
    a: "Absolutely. Association management is a core service — from year-round member engagement to annual meetings, sponsorship, and governance-friendly logistics.",
  },
  {
    q: "How fast can we get a proposal?",
    a: "A scoped conversation within one business day of your request, and a preliminary approach within a week for most programs.",
  },
];

// ═══════════════════════════════════════════════════════
// LogoTilt3D — Interactive 3D logo card with cursor-follow tilt
// ═══════════════════════════════════════════════════════
function LogoTilt3D() {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 6, y: -12 }); // default resting angles

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;   // -0.5 → 0.5
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    // Amplify a bit; invert Y so cursor "attracts" the tilt
    setTilt({ x: -py * 18, y: px * 22 });
  };
  const onLeave = () => setTilt({ x: 6, y: -12 });

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="commitments-logo-3d"
      className="relative"
      style={{ perspective: "1200px" }}
    >
      <div className="animate-[float3d_7s_ease-in-out_infinite]">
        <div
          className="rounded-3xl transition-transform duration-200 ease-out"
          style={{
            transformStyle: "preserve-3d",
            transform: `rotateY(${tilt.y}deg) rotateX(${tilt.x}deg)`,
            filter:
              "drop-shadow(0 30px 45px rgba(6,102,182,0.35)) drop-shadow(0 12px 22px rgba(6,182,212,0.25))",
          }}
        >
          <div
            className="relative rounded-3xl bg-gradient-to-br from-white via-white to-slate-50 p-8 md:p-10 border border-white/60"
            style={{
              boxShadow:
                "0 25px 60px -20px rgba(2,50,110,0.35), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(2,50,110,0.05)",
            }}
          >
            <img
              src={LOGO_URL}
              alt="Magee Enterprises · Plan. Lead. Inspire."
              className="w-[240px] h-auto select-none pointer-events-none"
              draggable={false}
            />
          </div>
          <div
            aria-hidden
            className="absolute -bottom-8 left-4 right-4 h-16 rounded-full opacity-40"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(6,102,182,0.35) 0%, rgba(6,102,182,0) 70%)",
              filter: "blur(14px)",
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacityScroll = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [reelOpen, setReelOpen] = useState(false);

  return (
    <div className="bg-white text-[var(--text)]">
      <ReelLightbox open={reelOpen} onClose={() => setReelOpen(false)} />
      <Seo
        title="Full-Service Event & Meeting Planning Concierge · Event Technology Experts"
        description="Magee Enterprises is a full-service event & meeting planning concierge and event technology partner. Certified project management, registration, mobile, hybrid broadcast, and site selection for meetings, associations, and productions at scale."
        path="/"
        keywords="event planning, meeting planning, event concierge, event technology, event management company, corporate meeting planners, association management, hybrid event technology, registration platform, event mobile app"
        jsonLd={[
          ORGANIZATION_JSONLD,
          WEBSITE_JSONLD,
          LOCAL_BUSINESS_JSONLD,
          AGGREGATE_RATING_JSONLD,
          buildFaqJsonLd(faq),
        ]}
      />
      {/* ================= HERO CAROUSEL ================= */}
      <section
        ref={heroRef}
        className="relative pt-28 md:pt-32 px-4 md:px-6"
      >
        <HeroCarousel yImg={yImg} opacityScroll={opacityScroll} onReelOpen={() => setReelOpen(true)} />
      </section>

      {/* ================= CLIENT LOGOS ================= */}
      <ClientLogos />

      {/* ================= STRATEGY ================= */}
      <section
        id="strategy"
        className="relative py-20 md:py-32 overflow-hidden scroll-mt-28 bg-[var(--surface)]"
      >
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[560px] h-[560px] -top-32 -left-40 opacity-40" />
        <div className="brand-bloom brand-bloom-blue w-[480px] h-[480px] -bottom-32 -right-24 opacity-30" />
        <HexMark
          size={260}
          opacity={0.055}
          className="absolute -right-8 top-24 rotate-[14deg] pointer-events-none hidden lg:block"
        />
        <HexMark
          size={180}
          opacity={0.04}
          className="absolute left-6 bottom-16 -rotate-[10deg] pointer-events-none hidden md:block"
        />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-14">
          {/* Left: Kinetic headline */}
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <span className="chip">
                <span className="chip-dot" /> Our Strategy
              </span>
            </Reveal>
            <div className="mt-8 clip-fix">
              <MaskedReveal
                lines={["Strategy that scales", "with your culture."]}
                className="font-editorial tracking-tighter leading-[0.94] text-[13vw] md:text-[7.4vw] lg:text-[6.1vw]"
                stagger={0.09}
              />
            </div>
            <Reveal delay={0.35}>
              <p className="mt-8 text-[var(--text-muted)] text-lg leading-relaxed max-w-xl">
                Every event or program runs on a clear scope, desired timelines,
                and a budget tracked line-by-line — with successful deliverables,
                disciplined vendor management, and stakeholder alignment from
                kickoff through post-event reporting.
              </p>
            </Reveal>
            <Reveal delay={0.5}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/services" className="btn-primary">
                  Explore our services <ArrowRight size={14} />
                </Link>
                <Link to="/contact" className="btn-dark">
                  Start a strategy call <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Right: 4 numbered pillars */}
          <div className="col-span-12 lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 self-center">
            {[
              {
                icon: Beaker,
                k: "01 · Discovery",
                t: "Intake, not interviews.",
                d: "Goals, business value and objectives are reviewed before we recommend anything.",
              },
              {
                icon: ClipboardList,
                k: "02 · Blueprint",
                t: "One plan, every stakeholder.",
                d: "Registration, run-of-show, logistics, and tech — architected as one document.",
              },
              {
                icon: Rocket,
                k: "03 · Execution",
                t: "Certified PMs on the floor.",
                d: "Rehearsed, radio-checked, and staffed to make the day feel inevitable.",
              },
              {
                icon: TrendingUp,
                k: "04 · Growth",
                t: "Proof, not promises.",
                d: "Post-event data packaged for future successes — what to repeat, what to retire.",
              },
            ].map((p, i) => (
              <Reveal key={p.k} delay={0.12 + i * 0.08}>
                <div className="grad-border card-white p-6 h-full relative">
                  <span className="corner-cross" style={{ top: 10, right: 10 }} />
                  <div className="icon-tile mb-5">
                    <p.icon size={18} />
                  </div>
                  <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    {p.k}
                  </span>
                  <h3 className="mt-1 font-editorial text-[17px] leading-tight">
                    {p.t}
                  </h3>
                  <p className="mt-3 text-[13px] leading-relaxed text-[var(--text-muted)]">
                    {p.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FEATURE ICON CARDS ================= */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[520px] h-[520px] -top-40 -right-40" />
        <HexMark
          size={220}
          opacity={0.06}
          className="absolute -left-16 top-24 rotate-[8deg] pointer-events-none hidden md:block"
        />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-baseline justify-between mb-10 md:mb-14">
              <span className="chip">
                <Sparkles size={12} /> Why clients choose us
              </span>
              <span className="hidden md:block font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                Six commitments
              </span>
            </div>
          </Reveal>

          <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
            {/* Left — commitments grid (smaller cards) */}
            <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {featureCards.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.06}>
                  <div className="grad-border card-white p-4 md:p-5 h-full hover:border-[var(--accent)]/40 hover:shadow-[0_12px_40px_-16px_rgba(6,182,212,0.35)] transition-all duration-500 relative">
                    <span className="corner-cross" style={{ top: 8, right: 8 }} />
                    <div className="flex items-center justify-between mb-3 relative z-10">
                      <div className="icon-tile !w-9 !h-9">
                        <f.icon size={15} />
                      </div>
                      <span className="font-mono-brand text-[8.5px] uppercase tracking-[0.22em] text-[var(--text-muted)] flex items-center gap-1">
                        <span className="inline-block w-1 h-1 rounded-full bg-[var(--accent)]" />
                        /{String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-editorial text-[15px] md:text-lg leading-tight text-[var(--text)] relative">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-[var(--text-muted)] text-[12.5px] leading-relaxed relative">
                      {f.body}
                    </p>
                    {f.link && (
                      <Link
                        to={f.link}
                        data-testid={`feature-card-link-${i + 1}`}
                        className="mt-3 inline-flex items-center gap-1.5 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--accent)] hover:text-[var(--text)] transition-colors group/link"
                      >
                        <Download size={12} className="group-hover/link:translate-y-0.5 transition-transform" />
                        {f.linkLabel || "Download"}
                      </Link>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Right — 3D floating logo (visible on all screens) */}
            <div className="col-span-12 lg:col-span-5 flex justify-center items-center relative min-h-[380px] md:min-h-[520px] mt-8 lg:mt-0">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[380px] h-[380px] rounded-full bg-gradient-to-br from-cyan-300/30 via-sky-400/20 to-blue-500/10 blur-3xl" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-40">
                <HexMark size={480} opacity={0.08} className="animate-[hexSpin_60s_linear_infinite]" />
              </div>

              <LogoTilt3D />

              {/* Floating micro-badges */}
              <div
                className="absolute top-6 right-4 animate-[float3d_6s_ease-in-out_infinite]"
                style={{ animationDelay: "0.4s" }}
              >
                <div className="rounded-2xl bg-white/90 backdrop-blur border border-white/60 shadow-[0_18px_36px_-18px_rgba(2,50,110,0.35)] px-4 py-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.9)]" />
                  <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]">
                    CMP · PMP · CHE
                  </span>
                </div>
              </div>
              <div
                className="absolute bottom-10 left-0 animate-[float3d_8s_ease-in-out_infinite]"
                style={{ animationDelay: "1.2s" }}
              >
                <div className="rounded-2xl bg-white/90 backdrop-blur border border-white/60 shadow-[0_18px_36px_-18px_rgba(2,50,110,0.35)] px-4 py-2">
                  <div className="font-mono-brand text-[9px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    SBA 8(a) certified
                  </div>
                  <div className="font-editorial text-sm text-[var(--text)]">Business Enterprise</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FLAGSHIP SERVICE ================= */}
      <section className="relative py-16 md:py-28 overflow-hidden">
        <div className="brand-bloom brand-bloom-cyan w-[600px] h-[600px] top-40 -left-40" />
        <div className="brand-bloom brand-bloom-navy w-[500px] h-[500px] -bottom-40 right-0 opacity-30" />
        <HexMark
          size={280}
          opacity={0.05}
          className="absolute right-4 -top-10 rotate-[18deg] pointer-events-none hidden lg:block"
        />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Chapter A · Flagship
                </span>
                <h2 className="mt-5 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Full-Service Strategic Meeting, Event &amp; Association Management.
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                Our premier &ldquo;flagship&rdquo; service. A certified project-management agency and on-site
                concierge — sourced, scoped, and executed as one continuous engagement.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-white overflow-hidden">
              <div className="grid grid-cols-12">
                <div className="col-span-12 lg:col-span-5 relative aspect-[16/10] lg:aspect-auto lg:min-h-[720px]">
                  <img
                    src={IMAGES.strategic}
                    alt="Strategic event management"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#050A14]/60 via-[#050A14]/20 to-transparent" />
                  <div className="absolute top-5 left-5 flex items-center gap-2">
                    <span className="chip !bg-white/90 !text-[var(--text)] !border-white/50">
                      <span className="chip-dot" /> Full Service Management
                    </span>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="font-editorial text-white text-2xl md:text-3xl leading-tight tracking-tight">
                      Expert services.
                      <span className="block text-cyan-300 italic font-medium">Complete logistics management.</span>
                    </p>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-7 p-6 md:p-10 border-t lg:border-t-0 lg:border-l border-[var(--border)]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      {
                        icon: Search,
                        title: "Sourcing",
                        body: "Venues, vendors and destinations negotiated against your KPIs.",
                      },
                      {
                        icon: ClipboardList,
                        title: "Project Management",
                        body: "Certified PMs owning timeline, risk and execution end-to-end.",
                      },
                      {
                        icon: Truck,
                        title: "Logistics Planning",
                        body: "Travel, transfers, hotel blocks and on-site movement.",
                      },
                      {
                        icon: BellRing,
                        title: "Concierge Solutions",
                        body: "Hourly, day-of or fully embedded — your call.",
                      },
                      {
                        icon: Video,
                        title: "Audio-Visual Productions",
                        body: "Full technical direction for stages and broadcasts.",
                      },
                      {
                        icon: MapPinned,
                        title: "On-Site Execution",
                        body: "Certified travel directors and floor teams.",
                      },
                    ].map((s, i) => (
                      <div
                        key={s.title}
                        className="group relative bg-white border border-[var(--border)] rounded-2xl p-5 hover:border-[var(--accent)]/50 hover:shadow-[0_12px_30px_-16px_rgba(6,182,212,0.4)] transition-all duration-500"
                      >
                        <div className="flex items-start gap-4">
                          <div className="icon-tile !w-11 !h-11 shrink-0">
                            <s.icon size={18} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--text-dim)]">
                                0{i + 1}
                              </span>
                            </div>
                            <h4 className="font-editorial text-[17px] md:text-lg leading-tight text-[var(--text)] mt-1">
                              {s.title}
                            </h4>
                            <p className="mt-2 text-[13px] text-[var(--text-muted)] leading-relaxed">
                              {s.body}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-[var(--border)]">
                    <Link
                      to="/services"
                      data-testid="home-flagship-cta"
                      className="btn-primary group"
                    >
                      Explore Flagship
                      <span className="mx-1 opacity-70">·</span>
                      <span className="inline-flex items-center gap-1 font-mono-brand text-[10px] uppercase tracking-[0.22em] opacity-90 group-hover:opacity-100">
                        <Sparkles size={11} /> and more!
                      </span>
                      <ArrowRight size={14} />
                    </Link>
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] hidden md:block">
                      /ref · 01
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= EVENT TECH SOLUTIONS — equal to flagship ================= */}
      <section className="relative py-16 md:py-28 overflow-hidden">
        <div className="brand-bloom brand-bloom-blue w-[520px] h-[520px] top-20 right-0 opacity-35" />
        <HexMark
          size={240}
          opacity={0.055}
          className="absolute -left-14 bottom-20 rotate-[-14deg] pointer-events-none hidden lg:block"
        />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip !bg-white/5 !text-cyan-700 !border-cyan-500/25">
                  <Zap size={12} /> Chapter B · Event Tech Concierge
                </span>
                <h2 className="mt-5 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Event Tech Concierge —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    Engineered as one solution.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                A full Concierge &ldquo;tech&rdquo; services solution. Registration,
                mobile, hybrid, data, and sponsorship — built so attendees feel a
                single continuous experience.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-dark relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern-dark opacity-40" />
              <div className="absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-cyan-500/15 blur-3xl" />
              <div className="absolute -bottom-24 -right-16 w-[500px] h-[500px] rounded-full bg-sky-500/15 blur-3xl" />

              <div className="relative grid grid-cols-12">
                {/* Icon-cards grid — left side */}
                <div className="col-span-12 lg:col-span-7 p-6 md:p-10 order-2 lg:order-1 border-t lg:border-t-0 lg:border-r border-white/10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {techStack.map((s, i) => (
                      <div
                        key={s.title}
                        className="group relative bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-cyan-400/40 rounded-2xl p-5 transition-all duration-500"
                      >
                        <div className="flex items-start gap-4">
                          <div className="icon-tile !w-11 !h-11 shrink-0">
                            <s.icon size={18} />
                          </div>
                          <div className="min-w-0">
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-cyan-300/70">
                              /{String(i + 1).padStart(2, "0")}
                            </span>
                            <h4 className="font-editorial text-[17px] md:text-lg leading-tight text-white mt-1">
                              {s.title}
                            </h4>
                            <p className="mt-2 text-[13px] text-white/60 leading-relaxed">
                              {s.body}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-white/10">
                    <Link to="/services/event-tech" className="btn-primary" data-testid="home-event-tech-cta">
                      Explore the Solution <ArrowRight size={14} />
                    </Link>
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-white/50 hidden md:block">
                      /ref · 02 · Concierge &ldquo;tech&rdquo; services
                    </span>
                  </div>
                </div>

                {/* Feature image + tagline — right side */}
                <div className="col-span-12 lg:col-span-5 relative aspect-[16/10] lg:aspect-auto lg:min-h-[720px] order-1 lg:order-2 overflow-hidden">
                  <img
                    src={IMAGES.tech}
                    alt="Event technology production"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-bl from-[#050A14]/40 via-[#050A14]/20 to-[#050A14]/70" />
                  <div className="absolute top-5 right-5 flex items-center gap-2">
                    <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/30 backdrop-blur">
                      <span className="chip-dot" /> Featured Service
                    </span>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="font-editorial text-white text-2xl md:text-3xl leading-tight tracking-tight">
                      Concierge &ldquo;tech&rdquo; services.
                      <span className="block text-cyan-300 italic font-medium">
                        One continuous experience.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= MARQUEE ================= */}
      <EditorialMarquee />

      {/* ================= BENEFITS ================= */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 hex-pattern-dense opacity-50 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] top-1/3 -right-32 opacity-40" />
        <div className="brand-bloom brand-bloom-warm w-[380px] h-[380px] bottom-1/4 -left-24 opacity-40" />
        <HexMark
          size={200}
          opacity={0.06}
          className="absolute -left-12 bottom-24 -rotate-12 pointer-events-none hidden md:block"
        />
        {/* Corner crosses */}
        <span className="corner-cross" style={{ top: 40, right: 40 }} />
        <span className="corner-cross" style={{ bottom: 40, left: 40 }} />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-baseline justify-between mb-12 md:mb-16 border-b border-[var(--border)] pb-6">
              <span className="chip">
                <Rocket size={12} /> Benefits
              </span>
              <span className="hidden md:block font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                Four commitments
              </span>
            </div>
          </Reveal>

          {manifesto.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <article className="grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-16 border-b border-[var(--border)] group">
                <div className="col-span-12 md:col-span-2">
                  <span className="font-mono-brand text-lg text-[var(--accent)] group-hover:text-[var(--text)] transition-colors duration-500">
                    {m.n}
                  </span>
                </div>
                <h3 className="col-span-12 md:col-span-7 font-editorial text-3xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-balance">
                  {m.title}
                </h3>
                <p className="col-span-12 md:col-span-3 text-[var(--text-muted)] leading-relaxed pt-2 md:pt-4 text-[15px]">
                  {m.body}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= FIT FINDER (Interactive Event Simulator) ================= */}
      <EventSimulator />

      {/* ================= FEATURED VIDEO ================= */}
      <FeaturedVideo />

      {/* ================= ACCREDITATIONS ================= */}
      <section
        aria-label="Accreditations"
        className="relative py-14 md:py-20 bg-white border-y border-[var(--border)]"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-14 text-center md:text-left">
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-[var(--text)]/25" />
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
              Accredited &amp; Certified
            </span>
            <span className="w-8 h-px bg-[var(--text)]/25" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {/* IATAN */}
            <a
              href="https://iatan.org"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 hover:opacity-100 transition-opacity"
              aria-label="IATAN — International Airlines Travel Agent Network"
              data-testid="accreditation-iatan"
            >
              <img
                src="/testimonials/person-4.jpeg"
                alt="IATAN — Accredited by International Airlines Travel Agent Network"
                className="h-16 md:h-20 w-auto opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                loading="lazy"
              />
              <div className="hidden md:flex flex-col">
                <span className="font-editorial text-base leading-tight text-[var(--text)]">
                  IATAN Accredited
                </span>
                <span className="text-[12px] text-[var(--text-muted)]">
                  Int&rsquo;l Airlines Travel Agent Network
                </span>
              </div>
            </a>

            {/* SBA 8(a) Certified */}
            <a
              href="https://www.sba.gov/federal-contracting/contracting-assistance-programs/8a-business-development-program"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 hover:opacity-100 transition-opacity"
              aria-label="U.S. Small Business Administration — 8(a) Certified"
              data-testid="accreditation-sba"
            >
              <img
                src="/home/hero-1.png"
                alt="U.S. Small Business Administration — 8(a) Certified"
                className="h-16 md:h-20 w-auto opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                loading="lazy"
              />
              <div className="hidden md:flex flex-col">
                <span className="font-editorial text-base leading-tight text-[var(--text)]">
                  SBA 8(a) Certified
                </span>
                <span className="text-[12px] text-[var(--text-muted)]">
                  U.S. Small Business Administration
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ================= PORTFOLIO BENTO ================= */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-32 -left-40 opacity-35" />
        <div className="brand-bloom brand-bloom-warm w-[420px] h-[420px] bottom-0 -right-24 opacity-30" />
        <HexMark size={240} opacity={0.055} className="absolute right-8 top-20 rotate-[16deg] pointer-events-none hidden lg:block" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip"><span className="chip-dot" /> Selected Portfolio</span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Programs we&rsquo;ve been{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">proud to deliver.</span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                Five case studies — from national employee recognition to global
                scientific convenings — each one measured, remembered, and repeated.
              </p>
            </div>
          </Reveal>

          {/* Bento grid */}
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {/* Case 01 — hero tile */}
            <Reveal className="col-span-12 lg:col-span-8">
              <Link to="/about#portfolio" className="group relative block rounded-2xl overflow-hidden border border-[var(--border)] aspect-[16/9] md:aspect-[21/10] hover:border-[var(--accent)]/40 transition-all">
                <img src="/portfolio/case01-incentives-verizon.jpg" alt="Verizon Excellence Awards" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/90 via-[#050A14]/40 to-transparent" />
                <span className="ribbon-badge">Flagship · #01</span>
                <div className="absolute top-6 left-6">
                  <span className="chip !bg-white/12 !text-white !border-white/25 backdrop-blur">
                    <span className="chip-dot" /> Verizon · National Incentives
                  </span>
                </div>
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/80 mb-2">
                    Case 01 · Employee Recognition
                  </p>
                  <h3 className="font-editorial text-xl sm:text-2xl md:text-5xl leading-[1.05] md:leading-[0.95] tracking-[-0.02em] max-w-2xl">
                    32 events. 8,000+ honorees.{" "}
                    <span className="hero-accent">Coast to coast.</span>
                  </h3>
                  <div className="mt-6 inline-flex items-center gap-2 text-white/80 group-hover:text-cyan-300 transition-colors">
                    <span className="font-mono-brand text-[11px] uppercase tracking-[0.22em]">Read case</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </Reveal>

            {/* Case 02 — vertical */}
            <Reveal className="col-span-12 sm:col-span-6 lg:col-span-4" delay={0.1}>
              <Link to="/about#portfolio" className="group relative block rounded-2xl overflow-hidden border border-[var(--border)] aspect-[3/4] md:aspect-auto md:h-full hover:border-[var(--accent)]/40 transition-all">
                <img src="/portfolio/hero_paris.jpg" alt="BioTech investigator meetings" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/90 via-[#050A14]/30 to-transparent" />
                <div className="absolute top-5 left-5">
                  <span className="chip !bg-white/12 !text-white !border-white/25 backdrop-blur">
                    <Beaker size={12} /> Life Sciences · CMP-Led
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/80 mb-1">Case 02</p>
                  <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                    Three cities, nine months,{" "}
                    <span className="text-cyan-300 italic font-medium">one flawless brief.</span>
                  </h3>
                </div>
              </Link>
            </Reveal>

            {/* Case 03 — horizontal */}
            <Reveal className="col-span-12 sm:col-span-6 lg:col-span-4" delay={0.15}>
              <Link to="/about#portfolio" className="group relative block rounded-2xl overflow-hidden border border-[var(--border)] aspect-[4/3] md:aspect-auto md:h-full hover:border-[var(--accent)]/40 transition-all">
                <img src="/portfolio/case03-pillar-college-group.jpg" alt="Pillar College inauguration" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/90 via-[#050A14]/30 to-transparent" />
                <div className="absolute top-5 left-5">
                  <span className="chip !bg-white/12 !text-white !border-white/25 backdrop-blur">
                    <GraduationCap size={12} /> University · Inauguration
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/80 mb-1">Case 03</p>
                  <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                    A one-day jubilee —{" "}
                    <span className="text-cyan-300 italic font-medium">planned quickly, executed decisively.</span>
                  </h3>
                </div>
              </Link>
            </Reveal>

            {/* Case 04 — full-width w/ dark card */}
            <Reveal className="col-span-12 lg:col-span-8" delay={0.05}>
              <Link to="/about#portfolio" className="group relative block rounded-2xl overflow-hidden aspect-[4/3] sm:aspect-[16/9] md:aspect-[21/9]">
                <img src="/portfolio/case04-aabb-2018-banner.jpg" alt="Novartis AABB annual meeting" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050A14]/90 via-[#050A14]/60 to-transparent" />
                <div className="absolute inset-y-0 left-0 flex flex-col justify-center p-5 sm:p-8 md:p-12 max-w-xl">
                  <span className="chip !bg-white/12 !text-white !border-white/25 backdrop-blur w-fit whitespace-nowrap !text-[10px] sm:!text-[11px]">
                    <Building2 size={12} /> Novartis · Trade Show
                  </span>
                  <p className="mt-4 sm:mt-5 font-mono-brand text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.24em] text-cyan-300/80">
                    Case 04 · Booth &amp; Attendees
                  </p>
                  <h3 className="mt-2 font-editorial text-2xl sm:text-3xl md:text-4xl leading-[1.05] md:leading-[1] tracking-[-0.02em] text-white">
                    600 attendees.{" "}
                    <span className="hero-accent">Three years running.</span>
                  </h3>
                  <div className="mt-5 sm:mt-6 inline-flex items-center gap-2 text-white/80 group-hover:text-cyan-300 transition-colors">
                    <span className="font-mono-brand text-[11px] uppercase tracking-[0.22em]">Read case</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </Reveal>

            {/* Case 05 — square */}
            <Reveal className="col-span-12 lg:col-span-4" delay={0.15}>
              <Link to="/about#portfolio" className="group relative block rounded-2xl overflow-hidden border border-[var(--border)] aspect-square md:aspect-auto md:h-full hover:border-[var(--accent)]/40 transition-all">
                <img src="/portfolio/case05-vaccines-conference-banner.jpg" alt="Vaccines conference" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/90 via-[#050A14]/30 to-transparent" />
                <div className="absolute top-5 left-5">
                  <span className="chip !bg-white/12 !text-white !border-white/25 backdrop-blur">
                    <Sparkles size={12} /> Scientific · Global
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/80 mb-1">Case 05</p>
                  <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                    Where the vaccine field{" "}
                    <span className="text-cyan-300 italic font-medium">convenes.</span>
                  </h3>
                </div>
              </Link>
            </Reveal>
          </div>

          {/* View all */}
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-wrap items-center justify-between gap-6">
              <p className="text-[var(--text-muted)] max-w-xl">
                Every engagement lands differently, but the discipline is always the
                same: measurable outcomes, obsessive planning, and a room that
                feels effortless.
              </p>
              <Link to="/about#portfolio" className="btn-primary">
                See all case studies <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ================= TESTIMONIALS ================= */}
      <Testimonials />

      {/* ================= CLOSING CTA ================= */}
      <section className="relative py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-8 md:px-16 py-20 md:py-28 text-center">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
            <div className="relative">
              <img
                src={LOGO_URL}
                alt=""
                className="h-16 md:h-20 mx-auto bg-white/95 rounded-2xl p-3 mb-8"
              />
              <Reveal>
                <h2 className="font-editorial text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em] text-white text-balance max-w-4xl mx-auto">
                  Partner with certified experts to deliver{" "}
                  <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                    with confidence.
                  </span>
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 text-white/70 max-w-xl mx-auto">
                  Plan. Lead. Inspire — every time. We work for you.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                  <Link to="/contact" className="btn-primary">
                    Start a Project <ArrowRight size={16} />
                  </Link>
                  <Link to="/services" className="btn-dark border-white/20">
                    Explore Services
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="relative py-20 md:py-32 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-60 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 left-1/4 opacity-30" />
        <HexMark
          size={260}
          opacity={0.07}
          className="absolute -right-14 top-16 rotate-[24deg] pointer-events-none hidden lg:block"
        />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <Reveal>
              <span className="chip">
                <span className="chip-dot" /> Questions
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 md:mt-6 font-editorial text-3xl sm:text-4xl md:text-6xl leading-[1] tracking-[-0.03em]">
                Straight answers,
                <br />
                <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                  short list.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 md:mt-6 text-[var(--text-muted)] max-w-sm">
                Don&apos;t see yours? Drop us a note — we reply within one business day.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <Link to="/contact" className="btn-ghost mt-6 md:mt-8">
                Ask a question <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-7 min-w-0">
            <Reveal>
              <Accordion type="single" collapsible className="w-full space-y-3">
                {faq.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="bg-white border border-[var(--border)] rounded-2xl px-4 md:px-6 data-[state=open]:border-[var(--accent)]/40 data-[state=open]:shadow-[0_8px_30px_-12px_rgba(6,182,212,0.25)] transition-all"
                  >
                    <AccordionTrigger className="text-left font-editorial text-base sm:text-lg md:text-xl tracking-tight hover:no-underline py-4 md:py-5">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[var(--text-muted)] leading-relaxed pb-5 pr-2 md:pr-6 text-sm md:text-base">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
